import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";
import { Building2, FileText, MapPin, User, KeyRound, CheckCircle2, ChevronLeft } from "lucide-react";

const STEPS = [
  { id: 1, label: "Azienda", icon: Building2 },
  { id: 2, label: "Sede legale", icon: MapPin },
  { id: 3, label: "Referente", icon: User },
  { id: 4, label: "Account", icon: KeyRound },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "", vat_number: "", pec: "", sdi_code: "", iban: "", landline_phone: "",
    rea_code: "",
    visura_filename: "", visura_base64: "",
    legal_address_street: "", legal_address_city: "", legal_address_zip: "", legal_address_province: "", legal_address_country: "Italia",
    contact_first_name: "", contact_last_name: "", contact_fiscal_code: "", contact_role: "",
    contact_phone: "", contact_birth_date: "", contact_birth_place: "",
    email: "", password: "", password2: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onVisura = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("La visura deve essere in formato PDF"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Visura camerale max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, visura_filename: f.name, visura_base64: reader.result });
    reader.readAsDataURL(f);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.company_name) return "Nome azienda obbligatorio";
      if (!form.vat_number || form.vat_number.length !== 11) return "Partita IVA: 11 cifre";
      if (!form.pec || !form.pec.includes("@")) return "PEC obbligatoria";
      if (!form.sdi_code) return "Codice SDI obbligatorio";
      if (!form.iban) return "IBAN obbligatorio";
      if (!form.landline_phone) return "Telefono fisso obbligatorio";
    }
    if (step === 2) {
      if (!form.legal_address_street) return "Indirizzo obbligatorio";
      if (!form.legal_address_city) return "Città obbligatoria";
      if (!form.legal_address_zip) return "CAP obbligatorio";
      if (!form.legal_address_province) return "Provincia obbligatoria";
    }
    if (step === 3) {
      if (!form.contact_first_name) return "Nome referente obbligatorio";
      if (!form.contact_last_name) return "Cognome referente obbligatorio";
      if (!form.contact_fiscal_code || form.contact_fiscal_code.length !== 16) return "Codice fiscale: 16 caratteri";
      if (!form.contact_role) return "Ruolo obbligatorio";
      if (!form.contact_phone) return "Telefono referente obbligatorio";
    }
    if (step === 4) {
      if (!form.email || !form.email.includes("@")) return "Email obbligatoria";
      if (!form.password || form.password.length < 6) return "Password min 6 caratteri";
      if (form.password !== form.password2) return "Le password non coincidono";
    }
    return null;
  };

  const next = (e) => {
    e?.preventDefault();
    const err = validateStep();
    if (err) { toast.error(err); return; }
    if (step < 4) { setStep(step + 1); window.scrollTo(0, 0); }
    else submit();
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        full_name: `${form.contact_first_name} ${form.contact_last_name}`.trim(),
        vat_number: form.vat_number.replace(/\s/g, ""),
        contact_fiscal_code: form.contact_fiscal_code.toUpperCase().replace(/\s/g, ""),
        iban: form.iban.replace(/\s/g, "").toUpperCase(),
      };
      delete payload.password2;
      const result = await register(payload);
      if (result?.verification_required) {
        toast.success("Account creato! Controlla la tua email per il codice");
        nav(`/verify-email?email=${encodeURIComponent(result.email)}&onboarding=1`);
        return;
      }
      toast.success("Benvenuto in Verixa");
      nav("/onboarding/plan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore registrazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="orb orb-orange" style={{width: 600, height: 600, top: -200, right: -200, opacity: 0.2}}/>
      <div className="relative w-full max-w-3xl card-smart p-8 md:p-12" data-testid="register-card">
        <Link to="/" className="inline-flex mb-6"><LogoMark size={56} pulse/></Link>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8 -mx-2">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const done = step > s.id;
            const active = step === s.id;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex flex-col items-center px-2 ${active ? "text-[#3B82F6]" : done ? "text-green-600" : "text-[#999]"}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${active ? "border-[#3B82F6] bg-[#1E2347]" : done ? "border-green-600 bg-green-50" : "border-[#1F2540] bg-white"}`}>
                    {done ? <CheckCircle2 className="w-5 h-5"/> : <Icon className="w-5 h-5"/>}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-1 hidden sm:block">{s.label}</div>
                </div>
                {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${done ? "bg-green-600" : "bg-[#1F2540]"}`}/>}
              </React.Fragment>
            );
          })}
        </div>

        <div className="text-xs uppercase tracking-widest text-[#3B82F6] mb-1">Registrazione azienda · Step {step}/4</div>
        <h1 className="font-display text-3xl mb-1">{["Dati aziendali", "Sede legale", "Persona di riferimento", "Crea il tuo account"][step-1]}</h1>
        <p className="text-sm text-[#757575] mb-6">{[
          "Le informazioni necessarie per la fatturazione elettronica.",
          "L'indirizzo della sede legale registrato in Camera di Commercio.",
          "Il referente principale che gestirà la piattaforma per l'azienda.",
          "Le credenziali di accesso. Riceverai un codice via email per attivare l'account.",
        ][step-1]}</p>

        <form onSubmit={next} className="space-y-4">
          {step === 1 && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nome azienda*</Label>
                <Input value={form.company_name} onChange={set("company_name")} data-testid="reg-company-name" placeholder="Es. La Mia Azienda SRL" className="rounded-xl mt-1 h-11"/>
              </div>
              <div>
                <Label>Partita IVA*</Label>
                <Input value={form.vat_number} onChange={set("vat_number")} data-testid="reg-vat" placeholder="11 cifre" maxLength={11} className="rounded-xl mt-1 h-11 font-mono"/>
              </div>
              <div>
                <Label>Codice Univoco SDI*</Label>
                <Input value={form.sdi_code} onChange={set("sdi_code")} data-testid="reg-sdi" placeholder="Es. M5UXCR1" className="rounded-xl mt-1 h-11 font-mono uppercase"/>
              </div>
              <div className="md:col-span-2">
                <Label>PEC*</Label>
                <Input type="email" value={form.pec} onChange={set("pec")} data-testid="reg-pec" placeholder="azienda@pec.it" className="rounded-xl mt-1 h-11"/>
              </div>
              <div className="md:col-span-2">
                <Label>IBAN*</Label>
                <Input value={form.iban} onChange={set("iban")} data-testid="reg-iban" placeholder="IT60 X054 2811 1010 0000 0123 456" className="rounded-xl mt-1 h-11 font-mono uppercase tracking-wide"/>
              </div>
              <div className="md:col-span-2">
                <Label>Telefono fisso azienda*</Label>
                <Input type="tel" value={form.landline_phone} onChange={set("landline_phone")} data-testid="reg-landline" placeholder="+39 02 1234567" className="rounded-xl mt-1 h-11"/>
              </div>
              <div>
                <Label>Codice REA</Label>
                <Input value={form.rea_code} onChange={set("rea_code")} data-testid="reg-rea" placeholder="MI-1234567" className="rounded-xl mt-1 h-11 font-mono"/>
                <div className="text-xs text-[#757575] mt-1">Es. MI-1234567 (sigla provincia + numero)</div>
              </div>
              <div>
                <Label>Visura camerale (PDF · max 5MB)</Label>
                <label htmlFor="visura-upload" className="mt-1 flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-[#3B82F6] bg-[#1E2347] cursor-pointer text-sm text-[#3B82F6] hover:bg-[#FFE3CC] transition" data-testid="reg-visura-label">
                  <FileText className="w-4 h-4 flex-shrink-0"/>
                  <span className="truncate">{form.visura_filename || "Clicca per caricare PDF"}</span>
                  {form.visura_base64 && <CheckCircle2 className="w-4 h-4 ml-auto text-green-600 flex-shrink-0"/>}
                </label>
                <input id="visura-upload" type="file" accept="application/pdf" onChange={onVisura} className="hidden" data-testid="reg-visura"/>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <Label>Indirizzo (via, civico)*</Label>
                <Input value={form.legal_address_street} onChange={set("legal_address_street")} data-testid="reg-street" placeholder="Via Roma, 12" className="rounded-xl mt-1 h-11"/>
              </div>
              <div className="md:col-span-2">
                <Label>Città*</Label>
                <Input value={form.legal_address_city} onChange={set("legal_address_city")} data-testid="reg-city" placeholder="Milano" className="rounded-xl mt-1 h-11"/>
              </div>
              <div>
                <Label>Provincia*</Label>
                <Input value={form.legal_address_province} onChange={set("legal_address_province")} data-testid="reg-province" placeholder="MI" maxLength={2} className="rounded-xl mt-1 h-11 uppercase font-mono"/>
              </div>
              <div>
                <Label>CAP*</Label>
                <Input value={form.legal_address_zip} onChange={set("legal_address_zip")} data-testid="reg-zip" placeholder="20100" maxLength={5} className="rounded-xl mt-1 h-11 font-mono"/>
              </div>
              <div className="md:col-span-2">
                <Label>Paese</Label>
                <Input value={form.legal_address_country} onChange={set("legal_address_country")} data-testid="reg-country" className="rounded-xl mt-1 h-11"/>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nome*</Label>
                <Input value={form.contact_first_name} onChange={set("contact_first_name")} data-testid="reg-first" className="rounded-xl mt-1 h-11"/>
              </div>
              <div>
                <Label>Cognome*</Label>
                <Input value={form.contact_last_name} onChange={set("contact_last_name")} data-testid="reg-last" className="rounded-xl mt-1 h-11"/>
              </div>
              <div className="md:col-span-2">
                <Label>Codice fiscale*</Label>
                <Input value={form.contact_fiscal_code} onChange={set("contact_fiscal_code")} data-testid="reg-cf" maxLength={16} placeholder="RSSMRA80A01H501Z" className="rounded-xl mt-1 h-11 font-mono uppercase"/>
              </div>
              <div>
                <Label>Ruolo in azienda*</Label>
                <Input value={form.contact_role} onChange={set("contact_role")} data-testid="reg-role" placeholder="Es. Amministratore, CEO, HR Manager" className="rounded-xl mt-1 h-11"/>
              </div>
              <div>
                <Label>Telefono*</Label>
                <Input type="tel" value={form.contact_phone} onChange={set("contact_phone")} data-testid="reg-phone" placeholder="+39 333 1234567" className="rounded-xl mt-1 h-11"/>
              </div>
              <div>
                <Label>Data di nascita</Label>
                <Input type="date" value={form.contact_birth_date} onChange={set("contact_birth_date")} data-testid="reg-birth" className="rounded-xl mt-1 h-11"/>
              </div>
              <div>
                <Label>Luogo di nascita</Label>
                <Input value={form.contact_birth_place} onChange={set("contact_birth_place")} data-testid="reg-birthplace" placeholder="Roma (RM)" className="rounded-xl mt-1 h-11"/>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <Label>Email di accesso*</Label>
                <Input type="email" value={form.email} onChange={set("email")} data-testid="reg-email" placeholder="tu@azienda.it" className="rounded-xl mt-1 h-11"/>
                <div className="text-xs text-[#757575] mt-1">Userai questa email per accedere a Verixa</div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Password* (min 6)</Label>
                  <Input type="password" value={form.password} onChange={set("password")} data-testid="reg-password" minLength={6} className="rounded-xl mt-1 h-11"/>
                </div>
                <div>
                  <Label>Conferma password*</Label>
                  <Input type="password" value={form.password2} onChange={set("password2")} data-testid="reg-password2" minLength={6} className="rounded-xl mt-1 h-11"/>
                  {form.password && form.password2 && form.password !== form.password2 && <div className="text-xs text-red-600 mt-1">Le password non coincidono</div>}
                </div>
              </div>
              <div className="bg-[#0A0E27] border border-[#1F2540] rounded-xl p-4 text-xs text-[#555]">
                <FileText className="w-4 h-4 inline mr-1 text-[#3B82F6]"/>
                Attivando l'account accetti i Termini di Servizio e l'Informativa Privacy GDPR. Riceverai un codice di verifica via email per attivare il login.
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-[#1F2540] mt-6">
            <Button type="button" variant="ghost" onClick={()=>step > 1 ? setStep(step-1) : nav("/login")} className="rounded-full text-[#757575]" data-testid="reg-back">
              <ChevronLeft className="w-4 h-4 mr-1"/> {step > 1 ? "Indietro" : "Annulla"}
            </Button>
            <Button type="submit" disabled={loading} data-testid="reg-next" className="rounded-full h-11 px-8 bg-[#3B82F6] hover:bg-[#7C3AED] btn-glow shadow-md shadow-[#3B82F6]/25">
              {loading ? "Creazione…" : step < 4 ? "Continua" : "Crea account"}
            </Button>
          </div>
        </form>

        <p className="text-sm text-[#555] mt-6 text-center">Hai già un account? <Link to="/login" className="text-[#3B82F6] hover:underline">Accedi</Link></p>
      </div>
    </div>
  );
}
